import { gql, useMutation } from '@apollo/client';
import { Button, Callout, IconButton, Text, TextField } from '@radix-ui/themes';
import { jwtDecode } from 'jwt-decode';
import { Eye, EyeOff } from 'lucide-react';
import { Form } from 'radix-ui';
import { use, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { getPasswordValidationMessage, revalidateFields } from '../../../utils/security';
import { setAccessToken } from '../../accesstoken';
import Title from '../../elements/Title';
import LocaleContext from '../../hooks/LocaleContext';
import BackToHubert from '../../navbar/BackToHubert';
import { setExpectedUserId } from '../../sessionIdentity';

type SubmitData = {
  email: string;
  newPassword: string;
  confirmPassword: string;
};

const SET_NEW_PASSWORD = gql`
  mutation SetPassword($password: String!, $token: String!) {
    SetPassword(token: $token, password: $password) {
      accessToken
      user {
        firstName
        lastName
      }
    }
  }
`;

const initState = {
  email: '',
  userId: '',
  tokenError: '',
  password: '',
  retypedPassword: '',
  validated: false,
  loading: false,
  error: false,
  disabled: false,
  info: undefined,
};

function reducer(
  state: {
    tokenError?: string;
    info?: string;
    email?: string;
    disabled?: boolean;
    loading?: boolean;
  },
  action: {
    type: string;
    disabled?: boolean;
    password?: string;
    retypedPassword?: string;
    validated?: boolean;
    loading?: boolean;
    error?: string;
    info?: string;
    jwtData?: { userId: string; email: string; exp: number };
  },
) {
  switch (action.type) {
    case 'setDisabled':
      return { ...state, disabled: action.disabled };
    case 'setJwt':
      return {
        ...state,
        userId: action.jwtData?.userId,
        email: action.jwtData?.email,
        exp: action.jwtData ? action.jwtData.exp * 1000 : 0,
      };
    case 'setPassword':
      return {
        ...state,
        password: action.password,
      };
    case 'setRetypedPassword':
      return {
        ...state,
        retypedPassword: action.retypedPassword,
      };
    case 'setValidated':
      return {
        ...state,
        validated: action.validated,
      };
    case 'setLoading':
      return {
        ...state,
        loading: action.loading,
      };
    case 'setTokenError':
      return {
        ...state,
        tokenError: action.error,
      };
    case 'setInfo':
      return {
        ...state,
        info: action.info,
      };

    default:
      throw new Error(`No ${action.type} is defined`);
  }
}

const SetNewPassword = () => {
  const { t } = useTranslation();
  const [states, dispatch] = useReducer(reducer, initState);
  const { setLoggedIn } = use(LocaleContext);

  const navigate = useNavigate();
  const query = useQuery();
  const magicLink = query.get('token');
  const [SetPassword] = useMutation(SET_NEW_PASSWORD);

  useEffect(() => {
    let jwt: { userId: string; email: string; exp: number } | false;
    try {
      jwt = magicLink ? jwtDecode<{ userId: string; email: string; exp: number }>(magicLink) : false;
    } catch {
      jwt = false;
    }
    if (jwt) {
      dispatch({ type: 'setJwt', jwtData: jwt });
      dispatch({ type: 'setTokenError', error: '' });
    } else {
      dispatch({ type: 'setTokenError', error: t('setPassword.tokenError.invalid') });
    }
    setLoggedIn?.(false);
    setAccessToken('');
  }, []);

  const handleSubmit = async (event: { email: string; password: string; passwordConfirmation: string }) => {
    if (states.loading) return;
    dispatch({ type: 'setLoading', loading: true });

    SetPassword({
      variables: {
        password: event.password,
        token: magicLink,
      },
    })
      .then((response) => {
        dispatch({ type: 'setDisabled', disabled: true });

        dispatch({ type: 'setLoading', loading: false });

        setAccessToken(response.data.SetPassword.accessToken);
        setExpectedUserId(response.data.SetPassword.accessToken);
        setLoggedIn?.(true);
        dispatch({ type: 'setInfo', info: t('setPassword.info.success') });
        setTimeout(() => dispatch({ type: 'setInfo', info: t('setPassword.info.loggingIn') }), 2000);
        // }
        setTimeout(() => (query.get('redirect') ? navigate(query.get('redirect') || '/') : navigate('/')), 5000);
        // }
      })
      .catch((err) => {
        console.error(err);
        dispatch({ type: 'setTokenError', error: t('setPassword.error.generic') });
        dispatch({ type: 'setLoading', loading: false });
      });
  };

  return (
    <div className="vh-100 flex items-center justify-center">
      <Title title={t('setPassword.pageTitle')} />
      <BackToHubert />
      <div className="pb-12 w-3/4" style={{ maxWidth: '25rem' }}>
        <div className="text-center">
          <img src="/logo/hubert_logo_lockup_dark@2x.png" className="mw-100" alt={t('setPassword.logo.alt')} />
          <h2 style={{ fontSize: '1.9rem' }}>{t('setPassword.heading')}</h2>
        </div>
        {states.tokenError && (
          <Callout.Root color="red">
            <Callout.Text className="mb-0">{states.tokenError}</Callout.Text>
          </Callout.Root>
        )}
        {states.info && (
          <Callout.Root color="green">
            <Callout.Text className="mb-0">{states.info}</Callout.Text>
          </Callout.Root>
        )}

        {states.email && !states.disabled && <ChangePassword state={states} handleSubmit={handleSubmit} />}
      </div>
    </div>
  );
};

export default SetNewPassword;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ChangePassword = ({
  state,
  handleSubmit,
}: {
  state: { email?: string; loading?: boolean };
  handleSubmit: (e: { email: string; password: string; passwordConfirmation: string }) => void;
}) => {
  const { t } = useTranslation();
  const [invalidNewPasswordMessage, setInvalidNewPasswordMessage] = useState(
    t('setPassword.validation.invalidPasswordFallback'),
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  return (
    <Form.Root
      onSubmit={(formEvent) => {
        formEvent.preventDefault();

        const form = formEvent.currentTarget;

        revalidateFields([newPasswordRef.current, confirmPasswordRef.current]);

        if (!form.checkValidity()) {
          return;
        }

        const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;

        handleSubmit({
          email: formData.email.trim().toLowerCase(),
          password: formData.newPassword.trim(),
          passwordConfirmation: formData.confirmPassword.trim(),
        });
      }}
    >
      <Form.Field name="email" className="mb-4">
        <div className="flex items-baseline">
          <Form.Label asChild>
            <Text size="2">
              <strong>{t('setPassword.form.emailLabel')}</strong>
            </Text>
          </Form.Label>
        </div>
        <div className="flex items-center">
          <Form.Control asChild>
            <TextField.Root className="w-full" size="3" readOnly defaultValue={state.email} />
          </Form.Control>
        </div>
      </Form.Field>
      <Form.Field name="newPassword" className="mb-4">
        <div className="flex items-baseline justify-between">
          <Form.Label asChild>
            <Text size="2">
              <strong>{t('setPassword.form.newPasswordLabel')}</strong>
            </Text>
          </Form.Label>
          <Text size="1" color="red">
            <Form.Message match="valueMissing">{t('setPassword.form.newPasswordRequired')}</Form.Message>
            <Form.Message
              match={(value) => {
                const message = getPasswordValidationMessage(
                  value,
                  t,
                  'setPassword.validation.invalidPasswordFallback',
                );
                if (message) {
                  setInvalidNewPasswordMessage(message);
                  return true;
                }
                return false;
              }}
            >
              {invalidNewPasswordMessage}
            </Form.Message>
          </Text>
        </div>
        <div className="flex items-center">
          <Form.Control asChild>
            <TextField.Root
              ref={newPasswordRef}
              placeholder={t('setPassword.form.newPasswordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              required
              className="w-full"
              size="3"
            />
          </Form.Control>
          <IconButton
            type="button"
            size="3"
            variant="outline"
            color="gray"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <Eye /> : <EyeOff />}
          </IconButton>
        </div>
      </Form.Field>
      <Form.Field name="confirmPassword" className="mb-4">
        <div className="flex items-baseline justify-between">
          <Form.Label asChild>
            <Text size="2">
              <strong>{t('setPassword.form.confirmPasswordLabel')}</strong>
            </Text>
          </Form.Label>
          <Text size="1" color="red">
            <Form.Message match="valueMissing">{t('setPassword.form.confirmPasswordRequired')}</Form.Message>
            <Form.Message match={(value, formData) => value !== (formData.get('newPassword') as string)}>
              {t('setPassword.form.passwordsMustMatch')}
            </Form.Message>
          </Text>
        </div>
        <div className="flex items-center">
          <Form.Control asChild>
            <TextField.Root
              ref={confirmPasswordRef}
              placeholder={t('setPassword.form.confirmPasswordPlaceholder')}
              type={showPasswordConfirmation ? 'text' : 'password'}
              required
              className="w-full"
              size="3"
            />
          </Form.Control>
          <IconButton
            type="button"
            size="3"
            variant="outline"
            color="gray"
            onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
          >
            {showPasswordConfirmation ? <Eye /> : <EyeOff />}
          </IconButton>
        </div>
      </Form.Field>
      <Form.Submit asChild>
        <Button size="3" className="w-full" loading={state.loading} disabled={state.loading}>
          {t('setPassword.submitButton')}
        </Button>
      </Form.Submit>
    </Form.Root>
  );
};
